import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, model, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {finalize, map, Subscription} from 'rxjs';
import {MenuItem, MessageService} from 'primeng/api';
import {Button} from 'primeng/button';
import {Popover} from 'primeng/popover';
import {Select} from 'primeng/select';
import {Tooltip} from 'primeng/tooltip';
import {Menu} from 'primeng/menu';
import {CosoService} from '../coso.service';
import {CosoLiveService} from '../coso-live.service';
import {CosoChat, CosoContext} from '../datamodel';
import {CosoConfig} from '../../coso-config/datamodel';
import {CosoChatListComponent} from '../coso-chat-list/coso-chat-list.component';
import {CosoChatDetailComponent} from '../coso-chat-detail/coso-chat-detail.component';
import {environment} from '@app/environment';

@Component({
  selector: 'app-coso-chat',
  imports: [
    FormsModule,
    Button,
    Popover,
    Select,
    Tooltip,
    Menu,
    CosoChatListComponent,
    CosoChatDetailComponent
  ],
  templateUrl: './coso-chat.component.html',
  styleUrl: './coso-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CosoChatComponent implements OnInit, OnDestroy {
  @ViewChild('newChatPopover') newChatPopover?: Popover;
  @ViewChild(CosoChatDetailComponent) chatDetailComponent?: CosoChatDetailComponent;
  public avatar = model<string>('');
  @Input() fullScreen = false;
  @Input() standalone = false;
  @Output() toggleFullScreen = new EventEmitter<void>();
  @Output() closeChat = new EventEmitter<void>();

  chats: CosoChat[] = [];
  selectedChat: CosoChat | null = null;
  allowedCosoConfigs: CosoConfig[] = [];
  loadingChats = true;
  creatingChat = false;
  deletingChatId: number | undefined;
  renamingChatId: number | undefined;
  newChatConfig: CosoConfig | undefined;
  chatActionItems: MenuItem[] = [
    {
      label: 'Copy',
      icon: 'pi pi-copy',
      command: () => this.chatDetailComponent?.copyChatAsFormattedText()
    },
    {
      label: 'Save markdown',
      icon: 'pi pi-download',
      command: () => this.chatDetailComponent?.saveChatAsMarkdown()
    },
    {
      label: 'Add to Knowledge Base',
      icon: 'pi pi-database',
      command: () => this.chatDetailComponent?.openKnowledgeBaseDialogForChat()
    }
  ];

  private contextSub: Subscription | undefined;
  private allowedCosoConfigsSub: Subscription | undefined;
  private getChatsSub: Subscription | undefined;
  private createChatSub: Subscription | undefined;
  private deleteChatSub: Subscription | undefined;
  private renameChatSub: Subscription | undefined;
  private loadedUserId: number | undefined;
  private currentContext: CosoContext | undefined;
  protected readonly environment = environment;
  // True while a Live (voice/video) session holds the mic/camera
  protected readonly liveActive = toSignal(
    inject(CosoLiveService).activeMediaStream$.pipe(map(stream => !!stream)),
    {initialValue: false}
  );

  constructor(public cosoService: CosoService,
              private messageService: MessageService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.allowedCosoConfigsSub = this.cosoService.allowedCosoConfigs$.subscribe(configs => {
      this.allowedCosoConfigs = configs;
      console.log(this.allowedCosoConfigs)
      this.cdr.markForCheck();
    });

    this.contextSub = this.cosoService.context$.subscribe(context => {
      this.currentContext = context;
      if (context.id_user && context.id_user !== this.loadedUserId) {
        this.loadedUserId = context.id_user;
        this.loadChats(context.id_user);
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.contextSub?.unsubscribe();
    this.allowedCosoConfigsSub?.unsubscribe();
    this.getChatsSub?.unsubscribe();
    this.createChatSub?.unsubscribe();
    this.deleteChatSub?.unsubscribe();
    this.renameChatSub?.unsubscribe();
  }

  loadChats(userId: number) {
    this.loadingChats = true;
    this.getChatsSub?.unsubscribe();
    this.getChatsSub = this.cosoService.getCosoChats(userId).subscribe({
      next: chats => {
        this.chats = this.sortChats(chats ?? []);

        if(!this.chats.length) {
          this.createDefaultChat();
        } else {
          this.openChat(this.chats[0]);
        }

        this.loadingChats = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.loadingChats = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
        this.cdr.markForCheck();
      }
    });
  }

  openCreateChatOverlay(event: Event) {
    if (this.creatingChat) {
      return;
    }

    if (this.allowedCosoConfigs.length === 1) {
      this.createChat(this.allowedCosoConfigs[0]);
      return;
    }

    this.newChatConfig = this.getDefaultNewChatConfig();
    this.newChatPopover?.toggle(event);
    this.cdr.markForCheck();
  }

  canCreateNewChat(config = this.newChatConfig): boolean {
    const context = this.currentContext ?? this.cosoService.getCurrentContextSnapshot();
    return !!(context.id_user ?? this.cosoService.user?.user_id) && !!config && !this.creatingChat;
  }

  createChat(config = this.newChatConfig) {
    if (!this.canCreateNewChat(config)) {
      return;
    }

    const context = this.currentContext ?? this.cosoService.getCurrentContextSnapshot();
    const userId = context.id_user ?? this.cosoService.user?.user_id;
    const chat = new CosoChat();
    chat.id_user = userId;
    chat.chat_title = 'New chat';
    chat.c_date = new Date().toISOString();
    chat.m_date = chat.c_date;
    chat.id_coso_config = config?.coso_config_id;
    chat.language = 'it';

    this.creatingChat = true;
    this.newChatConfig = config;
    this.cdr.markForCheck();
    this.createChatSub?.unsubscribe();
    this.createChatSub = this.cosoService.createCosoChat(chat).subscribe({
      next: createdChat => {
        const normalizedChat = this.normalizeCreatedChat(chat, createdChat);
        this.chats = [normalizedChat, ...this.chats.filter(existing => existing.coso_chats_id !== normalizedChat.coso_chats_id)];
        this.creatingChat = false;
        this.newChatPopover?.hide();
        this.openChat(normalizedChat);
        this.cdr.markForCheck();
      },
      error: error => {
        this.creatingChat = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: false, life: 5000});
        this.cdr.markForCheck();
      }
    });
  }

  openChat(chat: CosoChat) {
    this.selectedChat = chat;
    this.cosoService.selectCosoChat(chat);
    this.cdr.markForCheck();
  }

  backToList() {
    this.selectedChat = null;
    this.cdr.markForCheck();
  }

  onChatUpdated(updatedChat: CosoChat) {
    this.chats = this.sortChats(this.chats.map(chat => {
      if (chat.coso_chats_id !== updatedChat.coso_chats_id) {
        return chat;
      }

      return {
        ...chat,
        ...updatedChat
      };
    }));

    if (this.selectedChat?.coso_chats_id === updatedChat.coso_chats_id) {
      this.selectedChat = {
        ...this.selectedChat,
        ...updatedChat
      };
    }

    this.cdr.markForCheck();
  }

  deleteChat(chat: CosoChat) {
    if (!chat.coso_chats_id || this.deletingChatId) {
      return;
    }

    this.deletingChatId = chat.coso_chats_id;
    this.deleteChatSub?.unsubscribe();
    this.deleteChatSub = this.cosoService.deleteCosoChat(chat).pipe(
      finalize(() => {
        this.deletingChatId = undefined;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: deleted => {
        if (!deleted) {
          return;
        }

        this.chats = this.chats.filter(existing => existing.coso_chats_id !== chat.coso_chats_id);
        if (this.selectedChat?.coso_chats_id === chat.coso_chats_id) {
          this.selectedChat = null;
        }
        if (!this.chats.length) {
          this.createDefaultChat();
        }
        this.cdr.markForCheck();
      },
      error: error => {
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: false, life: 5000});
        this.cdr.markForCheck();
      }
    });
  }

  renameChat(chat: CosoChat) {
    if (!chat.coso_chats_id || this.renamingChatId) {
      return;
    }

    this.renamingChatId = chat.coso_chats_id;
    this.renameChatSub?.unsubscribe();
    this.renameChatSub = this.cosoService.updateCosoChat(chat).pipe(
      finalize(() => {
        this.renamingChatId = undefined;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: savedChat => {
        const normalizedChat = savedChat?.coso_chats_id ? savedChat : chat;
        this.onChatUpdated(normalizedChat);
      },
      error: error => {
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: false, life: 5000});
        this.cdr.markForCheck();
      }
    });
  }

  private normalizeCreatedChat(draftChat: CosoChat, createdChat: CosoChat): CosoChat {
    return {
      coso_chats_id: createdChat?.coso_chats_id ?? draftChat.coso_chats_id,
      id_user: createdChat?.id_user ?? draftChat.id_user,
      c_date: createdChat?.c_date ?? draftChat.c_date,
      m_date: createdChat?.m_date ?? draftChat.m_date,
      chat_title: createdChat?.chat_title ?? draftChat.chat_title,
      id_coso_config: createdChat?.id_coso_config ?? draftChat.id_coso_config,
      language: createdChat?.language ?? draftChat.language,
    };
  }

  getConfigHint(chat: CosoChat): string {
    const config = this.allowedCosoConfigs.find(cosoConfig => cosoConfig.coso_config_id === chat.id_coso_config);
    return config?.flow || config?.module_name || 'Default context';
  }

  private getDefaultNewChatConfig(): CosoConfig | undefined {
    const context = this.currentContext ?? this.cosoService.getCurrentContextSnapshot();
    const defaultConfig = this.cosoService.getDefaultCosoConfig();
    const activeConfig = this.allowedCosoConfigs.find(config => config.coso_config_id === context.id_coso_config);

    return activeConfig ?? defaultConfig ?? this.allowedCosoConfigs[0];
  }

  private createDefaultChat() {
    this.createChat(this.getDefaultNewChatConfig());
  }

  private sortChats(chats: CosoChat[]): CosoChat[] {
    return [...chats].sort((a, b) => {
      const aDate = a.m_date || a.c_date;
      const bDate = b.m_date || b.c_date;
      return (bDate ? new Date(bDate).getTime() : 0) - (aDate ? new Date(aDate).getTime() : 0);
    });
  }
}
